import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  Alert
} from '@mui/material';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import BackupIcon from '@mui/icons-material/Backup';
import RestoreIcon from '@mui/icons-material/Restore';
import SyncIcon from '@mui/icons-material/Sync';
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { MoodleToMoodleMigrationWizard } from './wizards/MoodleToMoodleMigrationWizard';
import { BackupMoodleToS3Wizard } from './wizards/BackupMoodleToS3Wizard';
import { RestoreMoodleFromS3Wizard } from './wizards/RestoreMoodleFromS3Wizard';
import { ExportDraftToS3Wizard } from './wizards/ExportDraftToS3Wizard';
import { ImportDraftFromS3Wizard } from './wizards/ImportDraftFromS3Wizard';
import { TransferDraftWizard } from './wizards/TransferDraftWizard';
import { useJobs } from '../contexts/JobsContext';

interface WizardCard {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  estimatedTime: string;
  tags: string[];
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'info';
  available: boolean;
  comingSoon?: boolean;
}

export const WizardsSection: React.FC = () => {
  const [activeWizard, setActiveWizard] = useState<string | null>(null);

  const handleWizardComplete = (jobId: string, wizardName: string) => {
    // Close the wizard on completion
    // Job notifications are handled by the JobsPanel via SSE
    setActiveWizard(null);
  };

  const wizards: WizardCard[] = [
    {
      id: 'moodle-to-moodle',
      title: 'Moodle to Moodle Migration',
      description: 'Migrate courses and content directly from one Moodle instance to another',
      icon: <SyncAltIcon fontSize="large" />,
      estimatedTime: '5-10 min',
      tags: ['Migration', 'Courses', 'Direct Transfer'],
      color: 'primary',
      available: true
    },
    {
      id: 'backup-to-s3',
      title: 'Backup Moodle to S3',
      description: 'Create a backup of Moodle courses, categories, and activities to S3 storage',
      icon: <BackupIcon fontSize="large" />,
      estimatedTime: '3-8 min',
      tags: ['Backup', 'Export', 'S3'],
      color: 'success',
      available: true
    },
    {
      id: 'restore-from-s3',
      title: 'Restore Moodle from S3',
      description: 'Restore courses and content from S3 backup to a Moodle instance',
      icon: <RestoreIcon fontSize="large" />,
      estimatedTime: '5-10 min',
      tags: ['Restore', 'Import', 'Versioning'],
      color: 'info',
      available: true
    },
    {
      id: 'sync-content',
      title: 'Update Content (Moodle → S3 Sync)',
      description: 'Detect changes in Moodle and sync updated content to S3',
      icon: <SyncIcon fontSize="large" />,
      estimatedTime: '2-5 min',
      tags: ['Sync', 'Update', 'Diff'],
      color: 'warning',
      available: false,
      comingSoon: true
    },
    {
      id: 'prepare-environment',
      title: 'Prepare S3 Environment',
      description: 'Set up a new S3 environment with content from multiple sources',
      icon: <FolderSpecialIcon fontSize="large" />,
      estimatedTime: '5-15 min',
      tags: ['Setup', 'Multi-Source', 'Environment'],
      color: 'secondary',
      available: false,
      comingSoon: true
    },
    {
      id: 'export-rangeos-activity',
      title: 'Export RangeOS Activity',
      description: 'Export a RangeOS activity with associated courses and scenarios',
      icon: <UploadFileIcon fontSize="large" />,
      estimatedTime: '3-8 min',
      tags: ['RangeOS', 'CMI5', 'Activities'],
      color: 'primary',
      available: false,
      comingSoon: true
    },
    {
      id: 'export-draft-to-s3',
      title: 'Export Draft Scenario to S3',
      description: 'Export a RangeOS draft scenario to S3 storage for backup or transfer',
      icon: <CloudUploadIcon fontSize="large" />,
      estimatedTime: '1-3 min',
      tags: ['RangeOS', 'Draft', 'Export', 'S3'],
      color: 'success',
      available: true
    },
    {
      id: 'import-draft-from-s3',
      title: 'Import Draft Scenario from S3',
      description: 'Import a draft scenario from S3 storage into a RangeOS instance',
      icon: <CloudDownloadIcon fontSize="large" />,
      estimatedTime: '1-3 min',
      tags: ['RangeOS', 'Draft', 'Import', 'S3'],
      color: 'info',
      available: true
    },
    {
      id: 'transfer-draft',
      title: 'Transfer Draft Between RangeOS',
      description: 'Directly transfer a draft scenario from one RangeOS instance to another',
      icon: <CompareArrowsIcon fontSize="large" />,
      estimatedTime: '2-4 min',
      tags: ['RangeOS', 'Draft', 'Transfer', 'Migration'],
      color: 'primary',
      available: true
    }
  ];

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Wizards
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Guided workflows to help you accomplish common tasks with step-by-step instructions.
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Tip:</strong> Wizards automatically save your progress. If you close a wizard before completing it,
            you'll be prompted to resume where you left off when you return.
          </Typography>
        </Alert>
      </Box>

      <Grid container spacing={3}>
        {wizards.map((wizard) => (
          <Grid item xs={12} md={6} lg={4} key={wizard.id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                opacity: wizard.available ? 1 : 0.7,
                '&:hover': wizard.available ? {
                  boxShadow: 4,
                  transform: 'translateY(-4px)',
                  transition: 'all 0.2s ease-in-out'
                } : {}
              }}
            >
              {wizard.comingSoon && (
                <Chip
                  label="Coming Soon"
                  size="small"
                  color="default"
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1
                  }}
                />
              )}

              <CardContent sx={{ flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2,
                    color: `${wizard.color}.main`
                  }}
                >
                  {wizard.icon}
                  <Typography variant="h6" sx={{ ml: 1 }} fontWeight="bold">
                    {wizard.title}
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" paragraph>
                  {wizard.description}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {wizard.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Estimated time: {wizard.estimatedTime}
                </Typography>
              </CardContent>

              <CardActions>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!wizard.available}
                  onClick={() => setActiveWizard(wizard.id)}
                >
                  {wizard.comingSoon ? 'Coming Soon' : 'Start Wizard'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Quick Actions
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Need to perform a single operation without a wizard? Access legacy quick actions here.
        </Typography>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Alert severity="info">
            <Typography variant="body2">
              Quick actions are available in the <strong>Browse</strong> section. Navigate to the content type
              you want to work with (S3 Browser, CMI5 Activities, etc.) to access single-step operations.
            </Typography>
          </Alert>
        </Paper>
      </Box>

      {/* Wizard Dialogs */}
      {activeWizard === 'moodle-to-moodle' && (
        <MoodleToMoodleMigrationWizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Moodle to Moodle Migration')}
        />
      )}

      {activeWizard === 'backup-to-s3' && (
        <BackupMoodleToS3Wizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Backup to S3')}
        />
      )}

      {activeWizard === 'restore-from-s3' && (
        <RestoreMoodleFromS3Wizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Restore from S3')}
        />
      )}

      {/* Placeholder for other wizards */}
      {activeWizard === 'sync-content' && (
        <Alert severity="info" onClose={() => setActiveWizard(null)} sx={{ mt: 2 }}>
          Sync Content wizard is coming soon!
        </Alert>
      )}

      {activeWizard === 'prepare-environment' && (
        <Alert severity="info" onClose={() => setActiveWizard(null)} sx={{ mt: 2 }}>
          Prepare Environment wizard is coming soon!
        </Alert>
      )}

      {activeWizard === 'export-rangeos-activity' && (
        <Alert severity="info" onClose={() => setActiveWizard(null)} sx={{ mt: 2 }}>
          Export RangeOS Activity wizard is coming soon!
        </Alert>
      )}

      {activeWizard === 'export-draft-to-s3' && (
        <ExportDraftToS3Wizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Export Draft to S3')}
        />
      )}

      {activeWizard === 'import-draft-from-s3' && (
        <ImportDraftFromS3Wizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Import Draft from S3')}
        />
      )}

      {activeWizard === 'transfer-draft' && (
        <TransferDraftWizard
          open
          onClose={() => setActiveWizard(null)}
          onComplete={(jobId) => handleWizardComplete(jobId, 'Transfer Draft')}
        />
      )}
    </Box>
  );
};
